// How the dashboard reaches Firebase: production (the default), the local
// emulators over HTTP (one developer machine or an HTTP test stand), or the
// emulators behind ONE HTTPS origin of a test stand (e.g. a Tailnet host),
// so the dashboard itself can be served over HTTPS without mixed content.
//
// Emulator modes are only for the demo project `demo-gastronaut`, which the
// Firebase SDKs never resolve to a real project, and they never fall back
// to production: a stand origin without emulator mode, an origin that is not
// plain HTTPS, or an emulator build without its own API address stops the
// app instead.
//
// Firebase JS SDK 8.10.1 (verified in its sources), per service over HTTPS:
// - Firestore: useEmulator() only sets `host` and `ssl: false`;
//   settings({ host, ssl: true }) is the same with TLS.
// - Auth: useEmulator(url) accepts `https://`; requests go to
//   <origin>/identitytoolkit.googleapis.com/…, /securetoken.googleapis.com/…
//   and /www.googleapis.com/identitytoolkit/….
// - Realtime Database: useEmulator() only sets host and `secure: false`; an
//   https databaseURL with `?ns=` is the same with TLS (<origin>/.ws, /.lp).
// - Storage: useEmulator() forces `http://`; the service's own `host` setter
//   takes `protocol://host:port` (requests to <origin>/v0/…).
// - Functions: useFunctionsEmulator(origin) takes the origin as given
//   (<origin>/demo-gastronaut/europe-west3/<name>).
//
// REACT_APP_EMULATOR_ORIGIN=same-origin takes the page's own origin at
// runtime (one test-stand proxy for page, API and emulators): https as above,
// plain http only on a loopback address. REACT_APP_EMULATOR_PROJECT_ID picks
// another demo project (default demo-gastronaut); it must start with demo-.

export const EMULATOR_PROJECT = 'demo-gastronaut';
const REGION = 'europe-west3';
const SAME_ORIGIN = 'same-origin';
const LOOPBACK_HOSTS = ['127.0.0.1', 'localhost'];

type Env = Record<string, string | undefined>;

export type EmulatorPlan =
  | { kind: 'production' }
  | { kind: 'local'; host: string; port: (service: string, fallback: number) => number; origin?: string }
  | { kind: 'secure'; origin: string; host: string };

const LOCAL_PORTS: Record<string, number> = { FIRESTORE: 8080, AUTH: 9099, DATABASE: 9000, STORAGE: 9199, FUNCTIONS: 5001 };

/** The demo project of an emulator build; anything that is not a demo- project stops the build. */
export const emulatorProjectFor = (env: Env) => {
  const project = (env.REACT_APP_EMULATOR_PROJECT_ID ?? '').trim() || EMULATOR_PROJECT;
  if (!/^demo-[a-z0-9-]{1,40}$/.test(project)) {
    throw new Error('REACT_APP_EMULATOR_PROJECT_ID muss ein demo-Projekt sein.');
  }
  return project;
};

const sameOriginPlan = (pageOrigin: string | undefined): EmulatorPlan => {
  const page = pageOrigin ? parsed(pageOrigin) : null;
  if (!page) throw new Error('same-origin braucht die Adresse der Seite.');
  if (page.protocol === 'https:') return { kind: 'secure', origin: page.origin, host: page.host };
  if (page.protocol === 'http:' && LOOPBACK_HOSTS.includes(page.hostname)) {
    const port = Number(page.port || 80);
    return { kind: 'local', host: page.hostname, port: () => port, origin: page.origin };
  }
  throw new Error('same-origin nur über https oder lokal (127.0.0.1/localhost).');
};

/** The transport the build asks for; misconfigurations throw instead of reaching production. */
export const emulatorPlan = (env: Env, pageOrigin?: string): EmulatorPlan => {
  const emulated = env.REACT_APP_USE_EMULATOR === 'true';
  const origin = (env.REACT_APP_EMULATOR_ORIGIN ?? '').trim();
  if (!emulated) {
    if (origin) throw new Error('REACT_APP_EMULATOR_ORIGIN gilt nur zusammen mit REACT_APP_USE_EMULATOR=true.');
    return { kind: 'production' };
  }
  if (origin === SAME_ORIGIN) return sameOriginPlan(pageOrigin);
  if (!origin) {
    return {
      kind: 'local',
      host: env.REACT_APP_EMULATOR_HOST || '127.0.0.1',
      port: (service, fallback) => Number(env[`REACT_APP_${service}_EMULATOR_PORT`] || fallback),
    };
  }
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    throw new Error('REACT_APP_EMULATOR_ORIGIN ist keine Adresse.');
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
    throw new Error('REACT_APP_EMULATOR_ORIGIN muss genau ein https-Ursprung sein (https://host[:port]).');
  }
  return { kind: 'secure', origin: url.origin, host: url.host };
};

/** The app configuration of an emulator build (always a demo project, dummy keys). */
export const emulatorAppConfig = (plan: Exclude<EmulatorPlan, { kind: 'production' }>, project = EMULATOR_PROJECT) => ({
  apiKey: 'demo-emulator-key',
  authDomain: `${project}.firebaseapp.com`,
  databaseURL:
    plan.kind === 'secure' || plan.origin
      ? `${plan.origin}/?ns=${project}`
      : `http://127.0.0.1:${LOCAL_PORTS.DATABASE}/?ns=${project}`,
  projectId: project,
  storageBucket: `${project}.appspot.com`,
  messagingSenderId: '000000000000',
  appId: '1:000000000000:web:demoemulator',
});

type FirebaseLike = {
  app: () => { options: { projectId?: string }; functions: (region: string) => any };
  firestore: () => any;
  auth: () => any;
  database: () => any;
  storage: () => any;
};

/** Points every Firebase service of the (already initialised) app at the chosen emulators. */
export const connectEmulators = (firebase: FirebaseLike, plan: EmulatorPlan, project = EMULATOR_PROJECT) => {
  if (plan.kind === 'production') return;
  if (firebase.app().options.projectId !== project || !project.startsWith('demo-')) {
    throw new Error(`Emulatoren nur mit dem Projekt ${project}.`);
  }
  if (plan.kind === 'local') {
    const { host, port } = plan;
    firebase.firestore().useEmulator(host, port('FIRESTORE', LOCAL_PORTS.FIRESTORE));
    firebase.auth().useEmulator(`http://${host}:${port('AUTH', LOCAL_PORTS.AUTH)}`, { disableWarnings: true });
    firebase.database().useEmulator(host, port('DATABASE', LOCAL_PORTS.DATABASE));
    firebase.storage().useEmulator(host, port('STORAGE', LOCAL_PORTS.STORAGE));
    firebase.app().functions(REGION).useEmulator(host, port('FUNCTIONS', LOCAL_PORTS.FUNCTIONS));
    return;
  }
  // Everything over the one HTTPS origin of the stand; the database through databaseURL.
  firebase.firestore().settings({ host: plan.host, ssl: true });
  firebase.auth().useEmulator(plan.origin, { disableWarnings: true });
  const storage = firebase.storage()?._delegate;
  if (!storage || !Object.getOwnPropertyDescriptor(Object.getPrototypeOf(storage), 'host')?.set) {
    throw new Error('Storage lässt sich nicht sicher mit dem Emulator verbinden.');
  }
  storage.host = plan.origin;
  firebase.app().functions(REGION).useFunctionsEmulator(plan.origin);
};

const parsed = (url: string) => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

// The shared APIs: both work on the production database.
const SHARED_API_HOSTS = ['api.gastronaut.ai', 'dev.api.gastronaut.ai'];

/**
 * The API address of a build. An emulator build talks only to its own API:
 * without REACT_APP_API_URL it stops instead of reaching the production (or
 * the shared dev) API with emulator sign-ins.
 */
export const apiBaseUrlFor = (env: Env, plan: EmulatorPlan, pageHost: string) => {
  const override = (env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  if (override === SAME_ORIGIN) {
    if (plan.kind === 'production' || !plan.origin) throw new Error('REACT_APP_API_URL=same-origin nur mit Emulator same-origin.');
    return plan.origin;
  }
  if (plan.kind === 'production') {
    return env.REACT_APP_API_TARGET === 'dev' ? 'https://dev.api.gastronaut.ai' : override || 'https://api.gastronaut.ai';
  }
  if (plan.kind === 'local' && pageHost.includes('localhost') && !override) return 'http://localhost:8000';
  if (env.REACT_APP_API_TARGET === 'dev') throw new Error('Emulator-Builds nutzen nie die geteilte Dev-API.');
  if (!override) throw new Error('Emulator-Builds brauchen REACT_APP_API_URL (die API des Teststands).');
  const api = parsed(override);
  if (!api || !['http:', 'https:'].includes(api.protocol)) throw new Error('REACT_APP_API_URL ist keine http(s)-Adresse.');
  if (SHARED_API_HOSTS.includes(api.hostname.toLowerCase().replace(/\.$/, ''))) {
    throw new Error('Emulator-Builds nutzen nie die produktive oder die geteilte Dev-API.');
  }
  if (plan.kind === 'secure' && api.origin !== plan.origin) {
    throw new Error('REACT_APP_API_URL muss auf dem Ursprung des Teststands liegen.');
  }
  return override;
};
